import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Swal from 'sweetalert2'
import { resetChangePasswordState, updatePassword } from '../../slice/authSlice'

const schema = yup.object().shape({
    current_password: yup.string().required('Current password is required'),
    new_password: yup.string().required('New password is required').min(6),
    confirm_password: yup
        .string()
        .oneOf([yup.ref('new_password')], 'Passwords must match')
        .required('Confirm password is required')
})

const ChangePassword = ({ isOpen, onClose }) => {
    const dispatch = useDispatch()
    const { loading } = useSelector(state => state.auth)

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    })

    const togglePassword = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }))
    }

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(schema)
    })

    // Lock body scroll when modal open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open')
        } else {
            document.body.classList.remove('modal-open')
        }
        return () => document.body.classList.remove('modal-open')
    }, [isOpen])

    const onSubmit = async (data) => {
        const res = await dispatch(updatePassword(data))

        if (res?.payload?.code === 200) {
            Swal.fire({
                icon: 'success',
                title: 'Password changed successfully',
                timer: 1500,
                showConfirmButton: false
            })
            reset()
            onClose()
            dispatch(resetChangePasswordState())
        } else {
            Swal.fire({
                icon: 'error',
                title: res?.payload?.message || 'Failed to change password'
            })
        }
    }

    if (!isOpen) return null

    return (
        <>
            {/* Backdrop */}
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1040 }}
                onClick={onClose}
            ></div>

            {/* Modal */}
            <div className="modal fade show d-block" style={{ zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5 className="modal-title">Change Password</h5>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="modal-body">

                                {/* Current Password */}
                                <div className="mb-3">
                                    <label>Current Password</label>
                                    <div className="input-group">
                                        <input
                                            type={showPassword.current ? 'text' : 'password'}
                                            className={`form-control ${errors.current_password ? 'is-invalid' : ''}`}
                                            {...register('current_password')}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => togglePassword('current')}
                                        >
                                            <i className={`fe-${showPassword.current ? 'eye-off' : 'eye'}`} />
                                        </button>
                                        {errors.current_password && (
                                            <div className="invalid-feedback">
                                                {errors.current_password.message}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* New Password */}
                                <div className="mb-3">
                                    <label>New Password</label>
                                    <div className="input-group">
                                        <input
                                            type={showPassword.new ? 'text' : 'password'}
                                            className={`form-control ${errors.new_password ? 'is-invalid' : ''}`}
                                            {...register('new_password')}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => togglePassword('new')}
                                        >
                                            <i className={`fe-${showPassword.new ? 'eye-off' : 'eye'}`} />
                                        </button>
                                        {errors.new_password && (
                                            <div className="invalid-feedback">
                                                {errors.new_password.message}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Confirm Password */}
                                <div className="mb-3">
                                    <label>Confirm Password</label>
                                    <div className="input-group">
                                        <input
                                            type={showPassword.confirm ? 'text' : 'password'}
                                            className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                            {...register('confirm_password')}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => togglePassword('confirm')}
                                        >
                                            <i className={`fe-${showPassword.confirm ? 'eye-off' : 'eye'}`} />
                                        </button>
                                        {errors.confirm_password && (
                                            <div className="invalid-feedback">
                                                {errors.confirm_password.message}
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={loading}
                                >
                                    {loading ? 'Updating...' : 'Update Password'}
                                </button>
                            </div>
                        </form>

                    </div>
                </div>
            </div>
        </>
    )
}

export default ChangePassword